import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import {
  IconBookings,
  IconBookingsActive,
  IconChat,
  IconChatActive,
  IconHome,
  IconHomeActive,
  IconTravellers,
  IconTravellersActive,
} from "@/components/Icons";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

const ROUTES = {
  search: "/",
  bookings: "/bookings",
  travelers: "/travelers",
  chat: "/chat",
} as const;

const eventMap = {
  search: CommonTrackingEvents.USER_PRESSED_SEARCH_NAVIGATION_BUTTON,
  travelers: CommonTrackingEvents.USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON,
  bookings: CommonTrackingEvents.USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON,
  chat: CommonTrackingEvents.USER_PRESSED_CHAT_NAVIGATION_BUTTON,
} as const;

export const Footer = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const matches = useMatchRoute();

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: keyof typeof ROUTES,
  ) => {
    rudderAnalytics.track(eventMap[newValue]);

    navigate({
      to: ROUTES[newValue],
    });
  };

  const getCurrentTabValue = (): keyof typeof ROUTES => {
    const routes = [
      { route: ROUTES.bookings, value: "bookings" },
      { route: ROUTES.travelers, value: "travelers" },
      { route: ROUTES.chat, value: "chat" },
    ] as const;

    for (const { route, value } of routes) {
      if (matches({ to: route, fuzzy: true })) {
        return value;
      }
    }

    return "search"; // if no other values match, we're on 'search' page
  };

  const currentTabValue = getCurrentTabValue();

  return (
    <BottomNavigation
      className="fixed bottom-0 z-20 w-full max-w-xl flex-row rounded-t-2xl border-t border-t-slate-300 pt-2 pb-2 text-xs"
      showLabels
      value={currentTabValue}
      onChange={handleChange}
      sx={{
        height: "calc(env(safe-area-inset-bottom) + var(--footer-height))",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 0.5rem)",
      }}
    >
      <BottomNavigationAction
        label={t("search")}
        icon={
          currentTabValue === "search" ? (
            <IconHomeActive aria-hidden />
          ) : (
            <IconHome aria-hidden />
          )
        }
        value="search"
      />
      <BottomNavigationAction
        label={t("myBookings")}
        icon={
          currentTabValue === "bookings" ? (
            <IconBookingsActive aria-hidden />
          ) : (
            <IconBookings aria-hidden />
          )
        }
        value="bookings"
      />
      <BottomNavigationAction
        label={t("travellers.title")}
        icon={
          currentTabValue === "travelers" ? (
            <IconTravellersActive aria-hidden />
          ) : (
            <IconTravellers aria-hidden />
          )
        }
        value="travelers"
      />
      <BottomNavigationAction
        label={t("chat.title")}
        icon={
          currentTabValue === "chat" ? (
            <IconChatActive aria-hidden />
          ) : (
            <IconChat aria-hidden />
          )
        }
        value="chat"
      />
    </BottomNavigation>
  );
};
